import React, { FunctionComponent, useState } from "react";
import {
  Box,
  Card,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  useTheme
} from "@mui/material";
import { strings } from "../../localization/LocalizedStrings";
import { PreferencesDialog } from "../PreferencesDialog";
import styles from "./styles.module.scss";
import { RoutesBuilder } from "../../models/RoutesBuilder";
import { DiscreetLink } from "../DiscreetLink";
import { MobileButtons } from "../MobileButtons";
import { DrawerHeader } from "../DrawerHeader";
import { BuyMeACoffeeButton } from "../BuyMeACoffeeButton";
import { Icon, IconType } from "../../icons";
import { PiperDivider } from "../PiperDivider";

export const LoginDrawer: FunctionComponent<ILoginDrawerProps> = ({
  isOpen,
  setIsOpen,
  onClickLoginAsGuest,
  onClickCreateNewUser,
  onClickRecoverPassword,
  onClickExplore,
  onClickPasskey
}) => {
  const theme = useTheme();
  const [isPreferencesDialogOpen, setIsPreferencesDialogOpen] = useState(false);
  const list = () => (
    <Box
      sx={{
        width: 310,
        height: "100%",
        overflow: "auto",
        backgroundColor: theme.palette.colors?.drawerBackgroundColor
      }}
      role="presentation"
    >
      <List className={styles.list}>
        <Card className={styles.sectionCard}>
          <ListItemButton
            key={"NewUser"}
            className={styles.listItem}
            onClick={onClickCreateNewUser}
          >
            <ListItemIcon>
              <Icon type={IconType.PersonAdd} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.login_create_user}
            />
          </ListItemButton>
          <PiperDivider />
          <ListItemButton
            key={"RecoverPassword"}
            className={styles.listItem}
            onClick={onClickRecoverPassword}
          >
            <ListItemIcon>
              <Icon type={IconType.Key} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.login_recover_password}
            />
          </ListItemButton>
          <PiperDivider />
          <ListItemButton key={"Guest"} className={styles.listItem} onClick={onClickLoginAsGuest}>
            <ListItemIcon>
              <Icon type={IconType.AssignmentInd} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.login_guest}
            />
          </ListItemButton>
        </Card>
        <Card className={styles.sectionCard}>
          <ListItemButton key={"Passkey"} className={styles.listItem} onClick={onClickPasskey}>
            <ListItemIcon>
              <Icon type={IconType.Passkey} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.signin_with_passkey_long}
            />
          </ListItemButton>
        </Card>
        <Card className={styles.sectionCard}>
          <ListItemButton key={"Explore"} className={styles.listItem} onClick={onClickExplore}>
            <ListItemIcon>
              <Icon type={IconType.Public} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.shortcuts_marketplace}
            />
          </ListItemButton>
        </Card>
        <Card className={styles.sectionCard}>
          <ListItemButton
            key={"Preferences"}
            className={styles.listItem}
            onClick={() => {
              setIsPreferencesDialogOpen(true);
            }}
          >
            <ListItemIcon>
              <Icon type={IconType.Settings} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.option_preferences}
            />
          </ListItemButton>
          <PiperDivider />
          <DiscreetLink to={RoutesBuilder.documentation.home()}>
            <ListItemButton key={"Documentation"} className={styles.listItem}>
              <ListItemIcon>
                <Icon type={IconType.Book2} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                className={styles.itemTitle}
                primary={strings.documentation}
              />
            </ListItemButton>
          </DiscreetLink>
        </Card>
        <MobileButtons />
        <BuyMeACoffeeButton />
      </List>
    </Box>
  );
  return (
    <div>
      <SwipeableDrawer
        anchor={"left"}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        variant={"temporary"}
      >
        <DrawerHeader setIsOpen={setIsOpen} />
        {list()}
      </SwipeableDrawer>
      <PreferencesDialog
        isOpen={isPreferencesDialogOpen}
        setIsOpen={setIsPreferencesDialogOpen}
        canRemoveAccount={false}
      />
    </div>
  );
};

export interface ILoginDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onClickLoginAsGuest: () => void;
  onClickCreateNewUser: () => void;
  onClickRecoverPassword: () => void;
  onClickExplore: () => void;
  onClickPasskey: () => void;
}
